import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import I10n from '../features/i10n';
import ProductInformationCache from '../persistence/description-cache';
import MsxIcons from '../features/msx-icons';
import { download } from '../utils/download';
import { logCSVEvent } from '../utils/analytics-logger';
import CountryConfiguration from './country_configuration';
import { getAppLocaleAsObject } from '../utils/app-locale';
import { PRIMARY_COLOR } from '../color-constants';
import NewBOMIsland from './bill_off_materials_island_rework';
import { PRECOMM_STATUS } from '../constants';

const Container = styled.div`
  width: 100%;
  @media (max-width: 800px) {
    display: none;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const Title = styled.h3`
  margin: 0px;
  font-size: 20px !important;
  line-height: 36px !important;
  font-weight: bold;
  color: #626469;
  @media (max-width: 800px) {
    line-height: inherit;
  }
  white-space: nowrap;
`;

const Icon = styled.span`
  display: inline-block;
  margin-left: 10px;
  color: black;
  text-decoration: none !important;
`;

const MobileIconDownload = styled(Icon)`
`;
const MobileContainer = styled.div`
  display: none;
  @media (max-width: 800px) {
    width: 100%;
    display: block;
  }
`;

const MobileBOMHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 10px 10px 0px;
  cursor: pointer;
  border-top: 1px solid #e7e6e6;
  border-bottom: 1px solid #e7e6e6;
`;

const MobileBOMContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  ${props => props.isMobileOpen ? 'max-height: 5000px;' : 'max-height: 0px;overflow: hidden;'}
  transition: max-height 0.3s;
`;

const MobileBOMPadding = styled.div`
  flex: 1 1 0px;
  padding: 10px 0px;
`;

const MobileIconWrapper = styled.div`
  transition: transform 0.3s;
  transform: ${props => props.isMobileOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  min-width: 32px;
  width: 32px;
  max-width: 32px;
  height: 32px;
  box-sizing: content-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MobileTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const ProductsAndIconWrapper = styled.div`
  display: flex;
  align-items: center;
  ${props => props.desktop && `justify-content: flex-end;
  flex: 1 1 0px;`}
`;

const AddToMyProductLabel = styled.span`
  white-space: nowrap;
`;

const AddToMyProductWrapper = styled.div`
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
  font-weight: 400;
  font-size: 12px;
  display: flex;
  align-items: center;

  ${props => props.favoritesDisabled ? `
    opacity: 0.5;
  ` : `
    cursor: pointer;
    :hover {
      ${AddToMyProductLabel} {
        text-decoration: underline;
      }
    }
  `}
`;

const TableWrapper = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: 12px;
`;

const TableIslandHeader = styled.thead`
  width: 100%;
`;

const TableIslandTitleRow = styled.tr``;

const TableIslandTitleCell = styled.td`
  background-color: #A0A4A8;
  color: white;
  width: 100%;
  vertical-align: middle;
`;

const IslandsTableBody = styled.tbody`
  position: relative;
`;

class NewBom extends React.Component {

  static propTypes = {
    configuration: PropTypes.object,
    references: PropTypes.array,
    islands: PropTypes.array,
    configurationName: PropTypes.string,
    preferredProductReference: PropTypes.string,
    datasheetTemplate: PropTypes.string,
    switchIsland: PropTypes.func,
    isLoading: PropTypes.bool,
    isWorkspaceAvailable: PropTypes.bool,
    unifiedCheckoutAvailable: PropTypes.bool,
    onAddToFavorites: PropTypes.func,
    favoritesDisabled: PropTypes.bool,
    allowBOMFavorites: PropTypes.bool,
    showAvailabilityStatus: PropTypes.bool,
    availabilityStatus: PropTypes.object,
    id: PropTypes.string
  }

  constructor(props) {
    super(props);
    this.state = {
      isMobileOpen: false
    }
  }

  buildIslandGroups = () => {
    return Object.keys(this.props.islands.map(this.renderIsland))
  }

  aggregatedReferences(references) {
    let maxNumber = 1;
    const map = {};
    references.forEach((referenceObj) => {
      if (map[referenceObj.reference]) {
        let obj = map[referenceObj.reference];
        map[referenceObj.reference] = { ...obj, quantity: obj.quantity + referenceObj.quantity }

      } else {
        map[referenceObj.reference] = {
          order: maxNumber++,
          reference: referenceObj.reference,
          quantity: referenceObj.quantity,
          description: referenceObj.description
        };
      }
    });

    const aggregatedReferences = [];
    Object.keys(map).forEach((referenceID) => {
      aggregatedReferences.push(map[referenceID]);
    });

    aggregatedReferences.sort(function (a, b) { return a.order - b.order });
    return aggregatedReferences;
  }

  renderIsland = (island, idx, commercializationStatusMap) => {
    return (
      <NewBOMIsland
        key={idx}
        island={island}
        preferredProductReference={this.props.preferredProductReference}
        datasheetTemplate={this.props.datasheetTemplate}
        switchIsland={this.props.switchIsland}
        isLoading={this.props.isLoading}
        availabilityStatus={this.props.availabilityStatus}
        showAvailabilityStatus={this.props.showAvailabilityStatus}
        commercializationStatusMap={commercializationStatusMap}
      >
      </NewBOMIsland>
    );
  };

  renderProducts = (commercializationStatusMap) => {
    return this.props.islands.map((island, idx) => this.renderIsland(island, idx, commercializationStatusMap));
  }

  renderIslandTable(commercializationStatusMap) {
    return (
      <TableWrapper>
        <TableIslandHeader>
          <TableIslandTitleRow>
            <TableIslandTitleCell colSpan={5}></TableIslandTitleCell>
          </TableIslandTitleRow>
        </TableIslandHeader>
        <IslandsTableBody>
          {this.renderProducts(commercializationStatusMap)}
        </IslandsTableBody>
      </TableWrapper>
    );
  };

  render = () => {
    const commercializationStatusMap = this.props.configuration.extractCommercializationStatusMap();

      return (
      <>
        <Container>
          <Header id='HeaderBom'>
            <Title>{I10n.get('bom')}</Title>
            {this.props.configuration.country !== getAppLocaleAsObject().country &&
              <CountryConfiguration country={this.props.configuration.country} />
            }
            <ProductsAndIconWrapper desktop={true}>
              {window.top === window.self && this.props.allowBOMFavorites && this.props.isWorkspaceAvailable && !this.props.unifiedCheckoutAvailable && <AddToMyProductWrapper favoritesDisabled={this.props.favoritesDisabled} onClick={() => {
                if (this.props.onAddToFavorites() && !this.props.favoritesDisabled) {
                  this.props.onAddToFavorites();
                }
              }}>
                <MsxIcons name="favorites"></MsxIcons>
                <AddToMyProductLabel>{I10n.get('saved_configuration.export.favorites')}</AddToMyProductLabel>
              </AddToMyProductWrapper>}
              <Icon title={I10n.get('bom.download')}
                id='DL-as-CSV'
                onClick={(e) => {
                  e.preventDefault();
                    this._download(commercializationStatusMap);
                }}
              >
                <MsxIcons option="button" name="download"></MsxIcons>
              </Icon>
            </ProductsAndIconWrapper>
          </Header>
          {this.renderIslandTable(commercializationStatusMap)}
        </Container>
        <MobileContainer isMobileOpen={this.state.isMobileOpen}>
          <MobileBOMHeader onClick={() => { this.setState({ isMobileOpen: !this.state.isMobileOpen }) }}>
            <MobileTitleWrapper>
              <Title>{I10n.get('bom')}
              </Title>
              {this.state.isMobileOpen && <ProductsAndIconWrapper>
                {window.top === window.self && this.props.allowBOMFavorites &&  this.props.isWorkspaceAvailable && !this.props.unifiedCheckoutAvailable && <AddToMyProductWrapper favoritesDisabled={this.props.favoritesDisabled} onClick={(event) => {
                  event.stopPropagation();
                  if (this.props.onAddToFavorites() && !this.props.favoritesDisabled) {
                    this.props.onAddToFavorites();
                  }
                }}>
                  <MsxIcons name="favorites"></MsxIcons>
                  <AddToMyProductLabel>{I10n.get('saved_configuration.export.favorites')}</AddToMyProductLabel>
                </AddToMyProductWrapper>}
                <MobileIconDownload title={I10n.get('bom.download')}
                  onClick={(e) => {
                    e.stopPropagation();
                    this._download(commercializationStatusMap);
                  }}
                >
                  <MsxIcons option="button" name="download"></MsxIcons>
                </MobileIconDownload>
              </ProductsAndIconWrapper>}
            </MobileTitleWrapper>
            <MobileIconWrapper isMobileOpen={this.state.isMobileOpen}>
              <MsxIcons name="down-arrow" size="small"></MsxIcons>
            </MobileIconWrapper>
          </MobileBOMHeader>
          <MobileBOMContent isMobileOpen={this.state.isMobileOpen}>
            <MobileBOMPadding>
              {this.renderIslandTable(commercializationStatusMap)}
            </MobileBOMPadding>
          </MobileBOMContent>
        </MobileContainer>
      </>
    );
  }

  _csvToArray = (str, delimiter = ";") => {
    const headers = str.slice(0, str.indexOf("\n")).split(delimiter);
    const rows = str.slice(str.indexOf("\n") + 1).split("\n");
    const arr = rows.map(function (row) {
      const values = row.split(delimiter);
      const el = headers.reduce(function (object, header, index) {
        object[header] = values[index];
        return object;
      }, {});
      return el;
    });

    return arr;
  }

  _download = async (commercializationStatusMap) => {
    const now = new Date();
    const rowsData = [];

    const columns = [
      I10n.get('bom.order'),
      I10n.get('bom.reference'),
      I10n.get('bom.description'),
      I10n.get('bom.quantity'),
      I10n.get('bom.warning')
    ];

    //Column names
    rowsData.push(columns);

    for (const aggregatedReference of this.aggregatedReferences(this.props.references)) {
      let description = ProductInformationCache.getDescription(aggregatedReference.reference)
        || aggregatedReference.description;

      let commercializationWarningMessage = '';
      if (commercializationStatusMap[aggregatedReference.reference] === PRECOMM_STATUS) {
        commercializationWarningMessage = I10n.get('precommercialization.tooltip.title');
      }

      const row = [
        aggregatedReference.order,
        aggregatedReference.reference,
        description,
        aggregatedReference.quantity,
        commercializationWarningMessage
      ];

      rowsData.push(row);
    }

    const columnSeparator = ';';
    const rows = [];
    for (const rowData of rowsData) {
      rows.push(rowData.map(d => {
        let escapedData = `${d}`;
        let needsDoubleQuote = false;
        if (escapedData.includes('"')) {
          needsDoubleQuote = true;
          escapedData = escapedData.split('"').join(`""`);
        } else if (escapedData.includes('\n') || escapedData.includes(columnSeparator)) {
          needsDoubleQuote = true;
        }
        return needsDoubleQuote ? `"${escapedData}"` : escapedData;
      }).join(columnSeparator));
    }

    const content = rows.join('\n');
    const dateFormatted = now.toISOString().split('.')[0];

    logCSVEvent(this.props.configuration, this.props.configuration.step);
    download(`${this.props.configurationName} - ${dateFormatted}.csv`, content);
  }

}

export default NewBom;
